/**
 * Permission Hook (Story 1.12)
 *
 * Reads the user's role from authStore and provides
 * permission-checking helpers for role-based UI rendering.
 *
 * Uses the same permission matrix as the backend (source of truth
 * is server-side — this is for UI convenience only).
 */

import { useAuthStore } from "../stores/authStore";

// ============================================================================
// Actions (mirror of server-side permissions.ts)
// KEEP IN SYNC WITH: apps/server/src/middleware/permissions.ts
// ============================================================================

export const Actions = {
  VIEW_DASHBOARD: "view:dashboard",
  VIEW_REPORTS: "view:reports",
  MANAGE_CLIENTS: "manage:clients",
  MANAGE_REPORTS: "manage:reports",
  MANAGE_INTEGRATIONS: "manage:integrations",
  MANAGE_TEAM: "manage:team",
  MANAGE_BILLING: "manage:billing",
  MANAGE_TENANT: "manage:tenant",
  DELETE_TENANT: "delete:tenant",
  // Epic 34 — Activity / Audit Log. VIEW_AUDIT_LOG = Owner+Admin (the timeline +
  // cold-read). VIEW_AUDIT_RAW_IPS = Owner only (un-masked IPs on event detail).
  VIEW_AUDIT_LOG: "view:audit_log",
  VIEW_AUDIT_RAW_IPS: "view:audit_raw_ips",
} as const;

export type Action = (typeof Actions)[keyof typeof Actions];
type Role = "owner" | "admin" | "viewer";

// ============================================================================
// Permission Matrix (mirror of server-side)
// KEEP IN SYNC WITH: apps/server/src/middleware/permissions.ts
// The server-side matrix is the source of truth. This is a UI-only mirror.
// ============================================================================

const PERMISSION_MATRIX: Record<Role, ReadonlySet<Action>> = {
  owner: new Set<Action>([
    Actions.VIEW_DASHBOARD,
    Actions.VIEW_REPORTS,
    Actions.MANAGE_CLIENTS,
    Actions.MANAGE_REPORTS,
    Actions.MANAGE_INTEGRATIONS,
    Actions.MANAGE_TEAM,
    Actions.MANAGE_BILLING,
    Actions.MANAGE_TENANT,
    Actions.DELETE_TENANT,
    Actions.VIEW_AUDIT_LOG, // Epic 34
    Actions.VIEW_AUDIT_RAW_IPS, // Epic 34 — Owner only
  ]),
  admin: new Set<Action>([
    Actions.VIEW_DASHBOARD,
    Actions.VIEW_REPORTS,
    Actions.MANAGE_CLIENTS,
    Actions.MANAGE_REPORTS,
    Actions.MANAGE_INTEGRATIONS,
    Actions.MANAGE_TEAM, // Story 1.13: Admin can manage team (invite, list, revoke)
    Actions.MANAGE_TENANT, // Story 14.5: Admin can manage tenant-level branding config
    Actions.VIEW_AUDIT_LOG, // Epic 34 — Admin can view the audit timeline (NOT raw IPs)
  ]),
  viewer: new Set<Action>([Actions.VIEW_DASHBOARD, Actions.VIEW_REPORTS]),
};

// ============================================================================
// Hook
// ============================================================================

export function usePermission() {
  const user = useAuthStore((state) => state.user);
  const role = user?.role ?? null;

  const hasPermission = (action: Action): boolean => {
    if (!role) return false;
    const allowed = PERMISSION_MATRIX[role];
    if (!allowed) return false;
    return allowed.has(action);
  };

  return {
    role,
    hasPermission,
    isOwner: role === "owner",
    isAdmin: role === "admin",
    isViewer: role === "viewer",
  };
}
